import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';

import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import { type OutreachConflictError } from '@apps/web/lib/outreach_utils';

type OutreachConflictAlertProps = {
    readonly conflict: OutreachConflictError;
    readonly timezone: string | undefined;
};

const OUTREACH_TYPE_LABELS: Record<string, string> = {
    callout: 'callout',
    recurring_shift: 'recurring shift outreach',
    shift_discovery: 'shift discovery outreach',
    clock_in: 'clock-in reminder',
    clock_out: 'clock-out reminder',
    shift_confirmation: 'shift confirmation',
    survey: 'survey',
    message: 'message',
};

const OUTREACH_STATUS_LABELS: Record<string, string> = {
    pending: 'Drafted',
    in_progress: 'In Progress',
    paused: 'Paused',
    filled: 'Filled',
    unfilled: 'Unfilled',
    cancelled: 'Archived',
};

function outreachTypeLabel(type: string): string {
    return OUTREACH_TYPE_LABELS[type] ?? type;
}

function outreachStatusLabel(status: string): string {
    return OUTREACH_STATUS_LABELS[status] ?? status;
}

export function OutreachConflictAlert({
    conflict,
    timezone,
}: OutreachConflictAlertProps) {
    const tz = timezone ?? 'UTC';

    return (
        <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{conflict.message}</AlertTitle>
            {conflict.shifts.length > 0 && (
                <AlertDescription>
                    <ul className="list-disc pl-4">
                        {conflict.shifts.map((s) => (
                            <li key={s.shift_id}>
                                {s.patient_name} (
                                {formatDateAndTimeWithWeekday(
                                    s.shift_start_time,
                                    tz,
                                )}
                                –{formatTime(s.shift_end_time, tz)})
                            </li>
                        ))}
                    </ul>
                </AlertDescription>
            )}
            {conflict.outreaches.length > 0 && (
                <AlertDescription>
                    <div className="mt-2 flex flex-col gap-1">
                        {conflict.outreaches.map((o) => (
                            <Link
                                key={o.outreach_id}
                                to="/outreaches/$callout-id"
                                params={{ 'callout-id': o.outreach_id }}
                                className="hover:bg-muted bg-background inline-flex w-fit items-center rounded-md border px-2 py-1 text-xs underline-offset-2 hover:underline"
                            >
                                View the active{' '}
                                {outreachTypeLabel(o.outreach_type)} (
                                {outreachStatusLabel(o.status)})
                            </Link>
                        ))}
                    </div>
                </AlertDescription>
            )}
        </Alert>
    );
}
